import React, { useMemo, useState } from "react";
import { Copy, Check, Download, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAppStore } from "../store/useAppStore";
import { maxPlatform } from "../services/maxPlatform";
import { PageHeader } from "../components/PageHeader";
import { PageShell } from "../components/PageShell";
import { GlassCard } from "../components/GlassCard";
import { QrCodeImage } from "../components/QrCodeImage";
import { printableQr } from "../utils/publicQr";
import { downloadQrPng } from "../utils/codeImages";

export function BoothSharePage() {
  const { t } = useTranslation();
  const {
    eventRole,
    exhibitorCompanyName,
    exhibitorBooth,
    exhibitorBoothQrCode,
    exhibitorCompanyQrCode,
  } = useAppStore();
  const [copied, setCopied] = useState<"booth" | "company" | null>(null);
  const [downloading, setDownloading] = useState<"booth" | "company" | null>(null);

  const boothOpaque = exhibitorBoothQrCode?.trim() || "";
  const companyOpaque = exhibitorCompanyQrCode?.trim() || "";
  const boothUrl = useMemo(
    () => (boothOpaque ? printableQr(boothOpaque) : ""),
    [boothOpaque],
  );
  const companyUrl = useMemo(
    () => (companyOpaque ? printableQr(companyOpaque) : ""),
    [companyOpaque],
  );

  if (eventRole !== "exhibitor") {
    return (
      <>
        <PageHeader title={t("profile.share_booth_qr")} back backTo="/my" />
        <PageShell>
          <p className="mt-10 text-center text-[13px] text-gray-400 px-6">
            {t("profile.role_visitor")}
          </p>
        </PageShell>
      </>
    );
  }

  const copy = async (url: string, kind: "booth" | "company") => {
    if (!url) return;
    await navigator.clipboard.writeText(url);
    setCopied(kind);
    setTimeout(() => setCopied(null), 2000);
    maxPlatform.hapticFeedback("light");
  };

  const download = async (url: string, kind: "booth" | "company", name: string) => {
    if (!url) return;
    setDownloading(kind);
    try {
      await downloadQrPng(url, name);
      toast.success(t("common.downloaded", { defaultValue: "QR сохранён" }));
    } catch {
      toast.error(t("visitor.loading_error"));
    } finally {
      setDownloading(null);
    }
  };

  return (
    <>
      <PageHeader title={t("profile.share_booth_qr")} back backTo="/my" />
      <PageShell>
        <div className="mt-4 space-y-4">
          {boothUrl && (
            <GlassCard variant="elevated" padding="lg">
              <p className="text-[14px] text-gray-900 mb-1">
                {exhibitorCompanyName || t("profile.booth_section")}
              </p>
              {exhibitorBooth && (
                <p className="text-[12px] text-gray-500 mb-3">
                  {t("visitor.booth")} {exhibitorBooth}
                </p>
              )}
              <div className="flex justify-center my-4">
                <QrCodeImage value={boothUrl} size={180} />
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() =>
                    void download(
                      boothUrl,
                      "booth",
                      `${exhibitorCompanyName || "booth"}-stenad`,
                    )
                  }
                  disabled={downloading === "booth"}
                  className="flex-1 h-11 rounded-xl bg-violet-500 text-white text-[14px] flex items-center justify-center gap-2 active:bg-violet-600 disabled:opacity-60"
                >
                  {downloading === "booth" ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Download size={16} />
                  )}
                  {t("common.download", { defaultValue: "Скачать" })}
                </button>
                <button
                  type="button"
                  onClick={() => void copy(boothUrl, "booth")}
                  className="h-11 w-11 rounded-xl bg-gray-100 text-gray-600 flex items-center justify-center"
                  aria-label={t("common.copy")}
                >
                  {copied === "booth" ? <Check size={16} /> : <Copy size={16} />}
                </button>
              </div>
            </GlassCard>
          )}

          {companyUrl && companyUrl !== boothUrl && (
            <GlassCard variant="default" padding="lg">
              <p className="text-[13px] text-gray-500 mb-3">Company QR</p>
              <div className="flex justify-center my-3">
                <QrCodeImage value={companyUrl} size={140} />
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() =>
                    void download(
                      companyUrl,
                      "company",
                      `${exhibitorCompanyName || "company"}-qr`,
                    )
                  }
                  disabled={downloading === "company"}
                  className="flex-1 h-10 rounded-xl bg-violet-500 text-white text-[13px] flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {downloading === "company" ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Download size={14} />
                  )}
                  {t("common.download", { defaultValue: "Скачать" })}
                </button>
                <button
                  type="button"
                  onClick={() => void copy(companyUrl, "company")}
                  className="h-10 w-10 rounded-xl bg-gray-100 text-gray-600 flex items-center justify-center"
                >
                  {copied === "company" ? <Check size={14} /> : <Copy size={14} />}
                </button>
              </div>
            </GlassCard>
          )}

          {!boothUrl && !companyUrl && (
            <p className="text-center text-[13px] text-gray-400 py-10">
              {t("visitor.loading_error")}
            </p>
          )}
        </div>
      </PageShell>
    </>
  );
}
